import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, ChevronDown, ChevronRight, Phone, Mail, MapPin,
  Layers, LayoutGrid, DoorOpen, SlidersHorizontal, Fence, Wrench,
  ArrowRight, Clock
} from "lucide-react";
import { NAV_ITEMS, SERVICES, OTHER_SERVICES, CONTACT_INFO, LOGO_URL } from "@/data/siteData";

const iconMap = { Layers, LayoutGrid, DoorOpen, SlidersHorizontal, Fence, Wrench };

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setExpandedMobile(null);
  }, [location]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setIsMobileMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-3 hidden lg:block">
        <div className="max-w-[1400px] mx-auto px-8 flex justify-between items-center">
          <div className="flex items-center gap-8 text-sm">
            {CONTACT_INFO.phones.map((phone, i) => (
              <a key={i} href={`tel:${phone}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
                <Phone className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-medium">{phone}</span>
              </a>
            ))}
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Mail className="w-4 h-4 text-[#D4AF37]" />
              <span>{CONTACT_INFO.email}</span>
            </a>
            <span className="flex items-center gap-2 text-white/60">
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span>{CONTACT_INFO.workingHours}</span>
            </span>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-sm">
            <MapPin className="w-4 h-4 text-[#D4AF37]" />
            <span>Dubai, UAE</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        data-testid="header"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'premium-glass border-b border-[#D4AF37]/30' : 'premium-glass border-b border-white/20'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo - More Prominent */}
            <Link to="/" data-testid="logo-link" className="flex-shrink-0">
              <img 
                src={LOGO_URL} 
                alt="Great International Glass & Aluminium" 
                className="h-14 lg:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center">
              {NAV_ITEMS.map((item) => (
                <div
                  key={item.label}
                  className={`relative group ${item.isMega || item.children ? '' : ''}`}
                >
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`flex items-center gap-1 px-3 py-3 text-sm font-semibold tracking-wide whitespace-nowrap transition-colors ${
                      location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                        ? 'text-[#D4AF37]'
                        : 'text-slate-700 hover:text-[#D4AF37]'
                    }`}
                  >
                    {item.label}
                    {(item.children || item.isMega) && (
                      <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                    )}
                  </Link>

                  {/* Regular Dropdown */}
                  {item.children && !item.isMega && (
                    <div className="absolute top-full left-0 w-64 bg-white border border-gray-200 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.15)] opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0 group-focus-within:translate-y-0 rounded-xl overflow-hidden">
                      <div className="py-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.path}
                            className="flex items-center justify-between px-5 py-3 text-sm text-slate-600 hover:bg-slate-50 hover:text-[#D4AF37] transition-colors"
                          >
                            <span>{child.label}</span>
                            <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-50" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Services Mega Menu - Improved */}
                  {item.isMega && (
                    <div 
                      className="absolute top-full left-1/2 -translate-x-1/2 w-[700px] bg-white border border-gray-200 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.2)] opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0 group-focus-within:translate-y-0 rounded-2xl overflow-hidden"
                      data-testid="mega-menu"
                    >
                      {/* Header */}
                      <div className="px-8 py-4 bg-slate-50 border-b border-gray-200">
                        <h3 className="text-lg font-bold text-slate-900">{item.label}</h3>
                        <p className="text-sm text-slate-500">
                          {item.label === "Services" 
                            ? "Comprehensive glass & aluminium solutions" 
                            : "Additional specialized contracting services"}
                        </p>
                      </div>

                      {/* Services Grid */}
                      <div className="p-6">
                        <div className="grid grid-cols-2 gap-1">
                          {(item.label === "Services" ? SERVICES : OTHER_SERVICES).map((service) => {
                            const IconComponent = iconMap[service.icon] || Layers;
                            return (
                              <Link
                                key={service.id}
                                to={item.label === "Services" ? `/services/${service.slug}` : `/other-services/${service.slug}`}
                                data-testid={`mega-menu-${service.id}`}
                                className="flex items-center gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors group/item"
                              >
                                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#0056D2]/10 flex items-center justify-center flex-shrink-0 group-hover/item:from-[#D4AF37] group-hover/item:to-[#D4AF37] transition-all">
                                  {IconComponent && <IconComponent className="w-5 h-5 text-[#D4AF37] group-hover/item:text-white transition-colors" strokeWidth={1.5} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-slate-900 text-sm group-hover/item:text-[#D4AF37] transition-colors truncate">
                                    {service.title}
                                  </h4>
                                  <p className="text-xs text-slate-400 mt-0.5 truncate">
                                    {service.description}
                                  </p>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="px-8 py-4 bg-slate-900 flex items-center justify-between">
                        <p className="text-sm text-white/70">
                          Need custom solutions?
                        </p>
                        <Link
                          to="/contact"
                          className="flex items-center gap-2 text-sm font-semibold text-[#D4AF37] hover:text-white transition-colors"
                        >
                          Contact Us <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <Link
              to="/contact"
              data-testid="header-cta"
              className="hidden lg:flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 font-bold text-sm uppercase tracking-wider hover:bg-[#C9A032] transition-colors"
            >
              Get Quote
            </Link>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-900"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu className="w-7 h-7" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-[998]"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 right-0 w-full max-w-sm h-full bg-white z-[999] overflow-y-auto"
              data-testid="mobile-menu"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <img src={LOGO_URL} alt="Logo" className="h-12" />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Close menu"
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6 text-slate-600" />
                </button>
              </div>

              {/* Navigation */}
              <div className="px-6 py-6">
                <nav className="space-y-1">
                  {NAV_ITEMS.map((item, index) => (
                    <div key={item.label} className="border-b border-gray-100">
                      {item.children || item.isMega ? (
                        <>
                          <button
                            onClick={() => setExpandedMobile(expandedMobile === item.label ? null : item.label)}
                            className="w-full flex items-center justify-between py-4 text-base font-semibold text-slate-800"
                          >
                            {item.label}
                            <ChevronDown 
                              className={`w-5 h-5 text-slate-400 transition-transform ${
                                expandedMobile === item.label ? 'rotate-180' : ''
                              }`} 
                            />
                          </button>
                          <AnimatePresence>
                            {expandedMobile === item.label && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="pb-4 space-y-1">
                                  {item.children && item.children.map((child) => (
                                    <Link
                                      key={child.label}
                                      to={child.path}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                      className="flex items-center gap-3 py-3 px-4 text-slate-600 hover:text-[#D4AF37] hover:bg-slate-50 transition-colors rounded"
                                    >
                                      <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                                      <span>{child.label}</span>
                                    </Link>
                                  ))}
                                  {item.isMega && (item.label === "Services" ? SERVICES : OTHER_SERVICES).map((service) => {
                                    const IconComponent = iconMap[service.icon] || Layers;
                                    return (
                                      <Link
                                        key={service.id}
                                        to={item.label === "Services" ? `/services/${service.slug}` : `/other-services/${service.slug}`}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="flex items-center gap-3 py-3 px-4 text-slate-600 hover:text-[#D4AF37] hover:bg-slate-50 transition-colors rounded"
                                      >
                                        <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                          {IconComponent && <IconComponent className="w-4 h-4 text-[#D4AF37]" />}
                                        </div>
                                        <span className="text-sm truncate">{service.title}</span>
                                      </Link>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <Link
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block py-4 text-base font-semibold text-slate-800 hover:text-[#D4AF37] transition-colors"
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  ))}
                </nav>

                {/* CTA */}
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full mt-8 bg-[#D4AF37] text-black px-6 py-4 font-bold text-sm uppercase tracking-wider"
                >
                  Get a Quote
                </Link>

                {/* Contact Info */}
                <div className="mt-8 pt-8 border-t border-gray-100 space-y-4">
                  {CONTACT_INFO.phones.map((phone, i) => (
                    <a key={i} href={`tel:${phone}`} className="flex items-center gap-3 text-slate-600">
                      <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 uppercase tracking-wider">{i === 0 ? "Mobile" : "Landline"}</span>
                        <p className="font-semibold text-slate-800">{phone}</p>
                      </div>
                    </a>
                  ))}
                  <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-3 text-slate-600">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <span className="text-xs text-slate-400 uppercase tracking-wider">Email</span>
                      <p className="font-semibold text-slate-800">{CONTACT_INFO.email}</p>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
